import type { User } from '../store/authSlice';

export type AuthRole = User['role'];

const REDIRECT_ORIGIN = 'https://stagecrew.app';
const MAX_REDIRECT_LENGTH = 512;
const SAFE_VENDOR_PATH = /^\/vendors\/[a-z0-9][a-z0-9-]{0,127}$/;
const SAFE_JOB_PATH = /^\/jobs\/\d+$/;
const SAFE_WORKER_PROFILE_PATH = /^\/workers\/[1-9][0-9]*$/;

const MATCH_STATUSES = new Set([
  'all',
  'matched',
  'application_opened',
  'applications',
  'applying',
  'applied',
  'auto_applied',
  'worker_dismissed',
]);

const MATCH_CONFIDENCES = new Set([
  'strong',
  'potential',
  'all',
]);

const getSingleAllowedValue = (
  params: URLSearchParams,
  name: string,
  allowedValues: Set<string>,
) => {
  const values = params.getAll(name);
  return values.length === 1 && allowedValues.has(values[0]) ? values[0] : '';
};

const roleCanUseWorkerDiscovery = (role?: AuthRole) => (
  role === undefined || role === 'worker'
);

const roleCanUseWorkerSubscription = (role?: AuthRole) => (
  role === undefined || role === 'worker'
);

const roleCanUseEmployerSubscription = (role?: AuthRole) => (
  role === undefined || role === 'employer'
);

const roleCanPostGig = (role?: AuthRole) => (
  role === undefined || role === 'employer'
);

const roleCanViewWorkerProfiles = (role?: AuthRole) => (
  role === undefined || role === 'employer' || role === 'admin'
);

const containsControlCharacter = (value: string) => Array.from(value).some(character => {
  const codePoint = character.codePointAt(0);
  return codePoint !== undefined && (codePoint <= 31 || codePoint === 127);
});

const hasMalformedPathSegment = (value: string) => {
  const rawPath = value.split(/[?#]/, 1)[0];
  return rawPath.split('/').some(segment => {
    let decodedSegment: string;
    try {
      decodedSegment = decodeURIComponent(segment);
    } catch {
      return true;
    }
    return (
      decodedSegment === '.'
      || decodedSegment === '..'
      || decodedSegment.includes('/')
      || decodedSegment.includes('\\')
      || containsControlCharacter(decodedSegment)
    );
  });
};

/**
 * Returns a canonical StageCrew destination or an empty string.
 *
 * Redirects are relative, bounded, fragment-free, explicitly allowlisted, and
 * role-checked once an authenticated role is known. This is defense in depth;
 * destination route guards remain responsible for authorization.
 */
export const getSafeAuthRedirect = (
  value: string | null,
  role?: AuthRole,
): string => {
  if (
    !value
    || value.length > MAX_REDIRECT_LENGTH
    || value !== value.trim()
    || !value.startsWith('/')
    || value.startsWith('//')
    || value.includes('\\')
    || containsControlCharacter(value)
    || hasMalformedPathSegment(value)
  ) {
    return '';
  }

  let parsed: URL;
  try {
    parsed = new URL(value, REDIRECT_ORIGIN);
  } catch {
    return '';
  }

  if (parsed.origin !== REDIRECT_ORIGIN || parsed.hash) {
    return '';
  }

  if (parsed.pathname === '/matches') {
    if (!roleCanUseWorkerDiscovery(role)) return '';

    const canonicalParams = new URLSearchParams();
    const status = getSingleAllowedValue(parsed.searchParams, 'status', MATCH_STATUSES);
    const confidence = getSingleAllowedValue(
      parsed.searchParams,
      'confidence',
      MATCH_CONFIDENCES,
    );
    if (status) canonicalParams.set('status', status);
    if (confidence) canonicalParams.set('confidence', confidence);
    const query = canonicalParams.toString();
    return query ? `/matches?${query}` : '/matches';
  }

  if (parsed.pathname === '/saved-jobs') {
    return roleCanUseWorkerDiscovery(role) ? '/saved-jobs' : '';
  }

  if (parsed.pathname === '/subscription') {
    return !parsed.search && roleCanUseWorkerSubscription(role)
      ? '/subscription'
      : '';
  }

  if (parsed.pathname === '/employer/subscription') {
    return !parsed.search && roleCanUseEmployerSubscription(role)
      ? '/employer/subscription'
      : '';
  }

  if (parsed.pathname === '/post-gig') {
    return roleCanPostGig(role) ? '/post-gig' : '';
  }

  if (SAFE_WORKER_PROFILE_PATH.test(parsed.pathname)) {
    return roleCanViewWorkerProfiles(role) ? parsed.pathname : '';
  }

  if (SAFE_JOB_PATH.test(parsed.pathname)) {
    return parsed.pathname;
  }

  if (SAFE_VENDOR_PATH.test(parsed.pathname)) {
    const claimValues = parsed.searchParams.getAll('claim');
    const hasValidClaim = claimValues.length === 1 && claimValues[0] === '1';
    if (hasValidClaim && (role === undefined || role === 'employer')) {
      return `${parsed.pathname}?claim=1`;
    }
    return parsed.pathname;
  }

  return '';
};

export const getDefaultAuthenticatedPath = (role?: AuthRole) => {
  if (role === 'worker') return '/dashboard';
  if (role === 'employer') return '/employer';
  if (role === 'admin') return '/admin';
  return '/';
};
